import type { Evidence, Report, SortKey } from './types';

/** The table renders at most this many rows; a whole vault holds thousands per snapshot set. */
export const TABLE_ROW_LIMIT = 2000;

export function filterEvidence(rows: Evidence[], query: string, kind: string, status: string, snapshot = 'all'): Evidence[] {
  const needle = query.trim().toLocaleLowerCase();
  return rows.filter((row) => {
    if (snapshot !== 'all' && row.snapshot !== snapshot) return false;
    if (kind !== 'all' && row.kind !== kind) return false;
    if (status !== 'all' && row.status !== status) return false;
    if (!needle) return true;
    return [
      row.archive,
      row.snapshot,
      row.first_seen_snapshot,
      row.last_seen_snapshot,
      row.source,
      row.internal_path,
      row.kind,
      row.owner_prefab_name,
      row.item_name,
      row.key_name,
      row.crafter_name,
      String(row.owner_prefab_hash),
      String(row.item_hash ?? ''),
      `${row.position.x} ${row.position.y} ${row.position.z}`,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(needle);
  });
}

export function sortEvidence(rows: Evidence[], key: SortKey, direction: 'asc' | 'desc'): Evidence[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    const a = valueFor(left, key);
    const b = valueFor(right, key);
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * factor;
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * factor;
  });
}

function valueFor(row: Evidence, key: SortKey): string | number {
  switch (key) {
    case 'stack': return row.stack ?? -1;
    case 'x': return row.position.x;
    case 'y': return row.position.y;
    case 'z': return row.position.z;
    case 'owner_prefab_name': return row.owner_prefab_name ?? '';
    case 'item_name': return row.item_name ?? '';
    case 'snapshot': return row.snapshot;
    case 'kind': return row.kind;
    case 'status': return row.status;
  }
}

/**
 * The snapshot filter for a new report: the current choice while the report still has it, else the
 * newest snapshot when showing every snapshot's rows would be too many to read, else all of them.
 */
export function defaultSnapshotFilter(report: Report, current: string): string {
  const snapshots = report.archives.map((archive) => archive.snapshot);
  if (current !== 'all' && snapshots.includes(current)) return current;
  return report.evidence.length > TABLE_ROW_LIMIT && snapshots.length > 1 ? snapshots[snapshots.length - 1] : 'all';
}

/** Shown only with exactly one world archive loaded — the case where status classification is a no-op. */
export function multiArchiveHint(archiveCount: number): string | null {
  return archiveCount === 1
    ? 'Only one save is loaded, so every record shows as observed. Add an earlier backup of the same world to classify rows as new, persisted, or removed_or_cleared.'
    : null;
}

export function timelineSummary(archiveCount: number): string {
  return archiveCount < 2
    ? 'One save reports each logical occurrence as observed. It cannot establish a universal ZDO creation time or continuity; add multiple dated saves for approximate first/last-seen history.'
    : 'Sorted saves classify each stack-excluded occurrence relative to the latest save as new, persisted, or removed_or_cleared. First/last-seen labels are approximate because chunk records do not carry a universal ZDO creation timestamp.';
}
